import type { QuoteResponse } from '../api/types'

/**
 * The quote is usable but the engine did not get to answer the question it wanted to.
 *
 * Never hidden, and never softened into "prices may vary". Each stage below means the plan
 * on screen was built with less information than normal, and a user about to sign is
 * entitled to know which:
 *
 *   single-route  the grid was cut to one row, so no split could be built at all
 *   stale-cache   this is a previous answer past its TTL, served because the node would
 *                 not answer a new one
 *
 * `pool-state-stale` appears among the reasons even on a successful quote: staleness was not
 * fatal here, but a refresher that has fallen behind degrades ranking silently, and silence
 * is the failure mode this project keeps hitting.
 */
export function DegradedBanner({ quote }: { quote: QuoteResponse }) {
  if (!quote.degraded.is) return null

  const headline =
    quote.degraded.stage === 'stale-cache'
      ? 'This is a cached quote, past its normal lifetime'
      : quote.degraded.stage === 'single-route'
        ? 'Quoted at reduced budget — no split was considered'
        : 'This quote is degraded'

  return (
    <div className="notice notice-warn">
      <div className="notice-title">{headline}</div>
      {quote.degraded.detail && <p className="notice-body">{quote.degraded.detail}</p>}
      <div className="notice-meta">
        <code>{quote.degraded.stage}</code>
        {quote.degraded.reasons.map((reason) => (
          <code key={reason}>{reason}</code>
        ))}
        {quote.degraded.nodeErrors > 0 && <span>{quote.degraded.nodeErrors} node errors</span>}
      </div>
    </div>
  )
}
